import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  PackageCheck,
  RefreshCw,
  MapPin,
  Store,
  Repeat,
  Check,
  AlertCircle,
  ChevronDown,
  Package,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import StatusChangeModal from "./StatusChangeModal";
import ExchangeStatusModal from "./ExchangeStatusModal";

/* ------------------------------------------------------------------ */
/*  CONFIG  (statuses follow the Order model / project PDF)            */
/* ------------------------------------------------------------------ */
const FLOW = ["New", "Accepted", "Processing", "Packed", "Shipped", "Delivered"];
const AFTER_SALES = ["Return", "Replacement", "Repair"];
const TABS = ["All", ...FLOW, ...AFTER_SALES, "Cancelled"];

const STATUS_STYLE = {
  New: { pill: "bg-sky-100 text-sky-700", text: "text-sky-700" },
  Accepted: { pill: "bg-indigo-100 text-indigo-700", text: "text-indigo-700" },
  Processing: { pill: "bg-amber-100 text-amber-700", text: "text-amber-700" },
  Packed: { pill: "bg-orange-100 text-orange-700", text: "text-orange-700" },
  Shipped: { pill: "bg-blue-100 text-blue-700", text: "text-blue-700" },
  Delivered: { pill: "bg-emerald-100 text-emerald-700", text: "text-emerald-700" },
  Return: { pill: "bg-purple-100 text-purple-700", text: "text-purple-700" },
  Replacement: { pill: "bg-violet-100 text-violet-700", text: "text-violet-700" },
  Repair: { pill: "bg-teal-100 text-teal-700", text: "text-teal-700" },
  Cancelled: { pill: "bg-red-100 text-red-600", text: "text-red-600" },
};

const PAYMENT_STYLE = {
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Failed: "bg-red-50 text-red-600 border-red-200",
};

const DATE_FILTERS = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 3 months", days: 90 },
  { label: "Last 12 months", days: 365 },
  { label: "All time", days: null },
];

const PER_PAGE = 10;
const IMG_BASE = process.env.REACT_APP_IMAGE_URL || "";

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "--";

const fmtMoney = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const shortId = (id = "") => id.slice(-6).toUpperCase();

const productName = (p) => p?.name?.en || (typeof p?.name === "string" ? p.name : "") || "Product unavailable";

const productImage = (p) => {
  const img = p?.images?.[0] ?? p?.image ?? p?.thumbnail;
  const url = typeof img === "string" ? img : img?.url || img?.secure_url;
  if (!url) return null;
  return /^https?:\/\//.test(url) ? url : `${IMG_BASE}${url}`;
};

const customerName = (u) => (u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() || "--" : "--");

const addressLines = (a) => {
  if (!a || typeof a !== "object") return [];
  const line1 = [a.addressLine1 || a.address || a.street, a.addressLine2].filter(Boolean).join(", ");
  const line2 = [a.city, a.state, a.pincode || a.zip || a.postalCode].filter(Boolean).join(", ");
  return [a.name || a.fullName, line1, line2, a.phone || a.mobile].filter(Boolean);
};

const headline = (order) => {
  switch (order.status) {
    case "Delivered":
      return `Delivered on ${fmtDate(order.updatedAt)}`;
    case "Cancelled":
      return `Cancelled on ${fmtDate(order.updatedAt)}`;
    case "New":
      return "Waiting for acceptance";
    default:
      return order.status;
  }
};

/* ------------------------------------------------------------------ */
/*  SMALL PIECES                                                       */
/* ------------------------------------------------------------------ */
const Tracker = ({ status }) => {
  if (status === "Cancelled") return null;
  const done = AFTER_SALES.includes(status) ? FLOW.length - 1 : FLOW.indexOf(status);
  return (
    <ol className="flex items-center w-full max-w-xl mt-3" aria-label="Order progress">
      {FLOW.map((step, i) => {
        const reached = i <= done;
        const current = i === done;
        return (
          <li key={step} className={`flex items-center ${i < FLOW.length - 1 ? "flex-1" : ""}`}>
            <div className="flex flex-col items-center">
              <span
                className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${reached ? "bg-emerald-500 border-emerald-500 text-white" : "bg-white border-slate-300"
                  } ${current ? "ring-4 ring-emerald-100" : ""}`}
              >
                {reached && <Check size={11} strokeWidth={3} />}
              </span>
              <span className={`mt-1 text-[10.5px] ${reached ? "text-slate-700 font-medium" : "text-slate-400"}`}>
                {step}
              </span>
            </div>
            {i < FLOW.length - 1 && (
              <span className={`flex-1 h-0.5 mx-1 -mt-4 ${i < done ? "bg-emerald-500" : "bg-slate-200"}`} />
            )}
          </li>
        );
      })}
    </ol>
  );
};

const HeaderField = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <div className="text-sm text-slate-800 mt-0.5">{children}</div>
  </div>
);

const ProductThumb = ({ product, small = false }) => {
  const [failed, setFailed] = useState(false);
  const src = productImage(product);
  return (
    <div className={`${small ? "w-[60px] h-[60px]" : "w-[88px] h-[88px]"} shrink-0 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center`}>
      {src && !failed ? (
        <img src={src} alt={productName(product)} onError={() => setFailed(true)} className="w-full h-full object-cover" />
      ) : (
        <Package className="text-slate-300" size={small ? 20 : 30} />
      )}
    </div>
  );
};

const OrderCardSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden animate-pulse">
    <div className="h-[72px] bg-slate-100" />
    <div className="p-5 flex gap-4">
      <div className="w-[88px] h-[88px] bg-slate-100 rounded-lg" />
      <div className="flex-1 space-y-3">
        <div className="h-4 w-1/3 bg-slate-100 rounded" />
        <div className="h-3 w-2/3 bg-slate-100 rounded" />
        <div className="h-3 w-1/2 bg-slate-100 rounded" />
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  ORDER CARD                                                         */
/* ------------------------------------------------------------------ */
const OrderCard = ({ order, onView, onStatus, onExchange }) => {
  const style = STATUS_STYLE[order.status] || STATUS_STYLE.New;
  const addr = addressLines(order.shippingAddress);
  const ex = order.exchangeRequest;

  return (
    <article className="bg-white rounded-xl border border-slate-200 shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:shadow-[0_8px_24px_-12px_rgba(16,24,40,0.15)] transition-shadow">
      {/* Header strip */}
      <header className="bg-slate-50 border-b border-slate-200 rounded-t-xl px-5 py-3.5 flex flex-wrap items-start gap-x-10 gap-y-3">
        <HeaderField label="Order placed">{fmtDate(order.createdAt)}</HeaderField>
        <HeaderField label="Total">{fmtMoney(order.totalAmount)}</HeaderField>

        <HeaderField label="Customer">
          <div className="relative group inline-block">
            <span className="text-blue-600 cursor-default border-b border-dotted border-blue-300">
              {customerName(order.user)}
            </span>
            {/* Ship-to popover */}
            <div className="hidden group-hover:block absolute left-0 top-full pt-2 z-20">
              <div className="w-64 bg-white border border-slate-200 rounded-lg shadow-xl p-3 text-[13px] leading-5">
                <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-slate-500 mb-1">
                  <MapPin size={12} /> Ship to
                </p>
                {addr.length ? (
                  addr.map((l, i) => (
                    <p key={i} className={i === 0 ? "font-semibold text-slate-800" : "text-slate-600"}>
                      {l}
                    </p>
                  ))
                ) : (
                  <p className="text-slate-500">Address not available</p>
                )}
                {order.user?.email && <p className="mt-2 text-slate-500 truncate">{order.user.email}</p>}
              </div>
            </div>
          </div>
        </HeaderField>

        <HeaderField label="Payment">
          <span className="flex items-center gap-2">
            <span className="uppercase text-xs font-medium text-slate-600">{order.paymentMethod || "cash"}</span>
            <span
              className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${PAYMENT_STYLE[order.paymentStatus] || PAYMENT_STYLE.Pending
                }`}
            >
              {order.paymentStatus || "Pending"}
            </span>
          </span>
        </HeaderField>

        <div className="ml-auto text-right">
          <p className="text-[11px] uppercase tracking-wide text-slate-500">Order # {shortId(order._id)}</p>
          <button
            onClick={() => onView(order)}
            className="text-sm text-blue-600 hover:text-blue-700 hover:underline mt-0.5"
          >
            View order details
          </button>
        </div>
      </header>

      {/* Body */}
      <div className="p-5 grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className={`text-lg font-bold ${style.text}`}>{headline(order)}</h2>
            <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${style.pill}`}>{order.status}</span>
            {ex?.isRequested && (
              <button
                onClick={() => onExchange(order)}
                className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
              >
                <Repeat size={11} /> Exchange: {ex.status}
              </button>
            )}
          </div>

          <Tracker status={order.status} />

          <ul className="mt-5 flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
            {(order.products || []).map((item, i) => (
              <li key={item._id || i} className="flex gap-3 p-3 w-[240px] shrink-0 border border-slate-200 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                <ProductThumb product={item.product} small />
                <div className="min-w-0 flex-1 flex flex-col justify-center">
                  <p className="text-[13px] font-medium text-slate-800 line-clamp-2 leading-snug">
                    {productName(item.product)}
                  </p>
                  <p className="text-[12px] text-slate-500 mt-1">
                    Qty {item.quantity} × {fmtMoney(item.price)}
                  </p>
                  <span
                    className={`inline-flex items-center gap-1 mt-1.5 w-fit text-[10px] font-medium px-2 py-0.5 rounded-full border ${item.supplier
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-white text-slate-600 border-slate-200"
                      }`}
                  >
                    <Store size={10} /> {item.supplier ? "Vendor item" : "Admin item"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Actions */}
        <aside className="flex flex-col gap-2.5 lg:border-l lg:border-slate-100 lg:pl-6">
          <button
            onClick={() => onView(order)}
            className="w-full rounded-lg py-2 text-sm font-medium text-white border border-transparent bg-[#b07d17] hover:bg-[#8c6312] shadow-sm transition-colors"
          >
            View order details
          </button>
          <button
            onClick={() => onStatus(order)}
            className="w-full rounded-lg py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 transition-colors"
          >
            Update status
          </button>
          {ex?.isRequested && (
            <button
              onClick={() => onExchange(order)}
              className="w-full rounded-lg py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors"
            >
              Review exchange
            </button>
          )}
        </aside>
      </div>
    </article>
  );
};

/* ------------------------------------------------------------------ */
/*  PAGE COMPONENT                                                     */
/* ------------------------------------------------------------------ */
const AdminOrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [dateFilter, setDateFilter] = useState(DATE_FILTERS[3]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [exchangeModalOpen, setExchangeModalOpen] = useState(false);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/orders/get-all-orders`);
      setOrders(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("We couldn't load the orders. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  /* search + date filter (tab counts are based on this) */
  const baseFiltered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const cutoff = dateFilter.days ? Date.now() - dateFilter.days * 86400000 : null;

    return orders.filter((o) => {
      if (cutoff && new Date(o.createdAt).getTime() < cutoff) return false;
      if (!q) return true;
      const haystack = [
        o._id,
        shortId(o._id),
        customerName(o.user),
        o.user?.email,
        o.user?.phone,
        ...(o.products || []).map((p) => productName(p.product)),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [orders, searchTerm, dateFilter]);

  const counts = useMemo(() => {
    const c = { All: baseFiltered.length };
    baseFiltered.forEach((o) => {
      c[o.status] = (c[o.status] || 0) + 1;
    });
    return c;
  }, [baseFiltered]);

  const filteredOrders = useMemo(
    () => (activeTab === "All" ? baseFiltered : baseFiltered.filter((o) => o.status === activeTab)),
    [baseFiltered, activeTab]
  );

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / PER_PAGE));
  const startIndex = (currentPage - 1) * PER_PAGE;
  const currentOrders = filteredOrders.slice(startIndex, startIndex + PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageNumbers = useMemo(() => {
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const list = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
    return list.reduce((acc, p, i) => {
      if (i && p - list[i - 1] > 1) acc.push("…");
      acc.push(p);
      return acc;
    }, []);
  }, [currentPage, totalPages]);

  const openStatus = (order) => {
    setSelectedOrder(order);
    setStatusModalOpen(true);
  };
  const openExchange = (order) => {
    setSelectedOrder(order);
    setExchangeModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#F3F6FB] p-6 font-sans">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h1 className="text-[28px] leading-8 font-extrabold text-white tracking-tight flex items-center gap-2.5">
              <PackageCheck className="text-[#c59a3f]" size={28} />
              Orders
            </h1>
            <p className="text-sm text-slate-500 mt-1.5">
              {loading ? "Loading orders…" : `${filteredOrders.length} order${filteredOrders.length === 1 ? "" : "s"} placed in ${dateFilter.label.toLowerCase()}`}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <select
                value={dateFilter.label}
                onChange={(e) => {
                  setDateFilter(DATE_FILTERS.find((d) => d.label === e.target.value));
                  setCurrentPage(1);
                }}
                aria-label="Placed in"
                className="appearance-none bg-white border border-slate-300 rounded-lg pl-3 pr-9 py-2 text-sm text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#d4af37]/50"
              >
                {DATE_FILTERS.map((d) => (
                  <option key={d.label}>{d.label}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-2.5 text-slate-500 pointer-events-none" />
            </div>
            <button
              onClick={fetchOrders}
              disabled={loading}
              className="flex items-center gap-2 bg-[#b07d17] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#8c6312] disabled:opacity-60 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex mb-4 shadow-sm rounded-lg overflow-hidden border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-[#d4af37]/50">
          <input
            type="text"
            placeholder="Search by order #, customer name, email, phone or product"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="flex-1 px-4 py-2.5 text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
          />
          <button
            aria-label="Search orders"
            className="px-5 bg-[#b07d17] border-l border-transparent text-white hover:bg-[#8c6312] transition-colors"
          >
            <Search size={18} />
          </button>
        </div>

        {/* Status tabs */}
        <div className="border-b border-slate-200 mb-5 overflow-x-auto">
          <div className="flex gap-1 min-w-max" role="tablist">
            {TABS.map((tab) => {
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-3 text-sm whitespace-nowrap border-b-[3px] -mb-px transition-colors ${active
                    ? "border-[#e47911] text-white font-semibold"
                    : "border-transparent text-slate-600 hover:text-white hover:border-slate-300"
                    }`}
                >
                  {tab}
                  <span className={`ml-1.5 text-xs ${active ? "text-[#c45500]" : "text-slate-400"}`}>
                    {counts[tab] || 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-5 text-sm">
            <AlertCircle size={18} />
            <span className="flex-1">{error}</span>
            <button onClick={fetchOrders} className="font-semibold underline">
              Try again
            </button>
          </div>
        )}

        {/* List */}
        <div className="space-y-5">
          {loading ? (
            [...Array(3)].map((_, i) => <OrderCardSkeleton key={i} />)
          ) : currentOrders.length > 0 ? (
            currentOrders.map((order) => (
              <OrderCard
                key={order._id}
                order={order}
                onView={(o) => navigate(`/admin/orders/${o._id}`)}
                onStatus={openStatus}
                onExchange={openExchange}
              />
            ))
          ) : (
            !error && (
              <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
                <Package className="mx-auto text-slate-300" size={44} />
                <p className="mt-3 text-slate-800 font-semibold">No orders found</p>
                <p className="text-sm text-slate-500 mt-1">
                  Try a different status, date range or search term.
                </p>
              </div>
            )
          )}
        </div>

        {/* Pagination */}
        {!loading && filteredOrders.length > PER_PAGE && (
          <nav className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3" aria-label="Pagination">
            <p className="text-sm text-slate-600">
              Showing {startIndex + 1}–{Math.min(startIndex + PER_PAGE, filteredOrders.length)} of {filteredOrders.length} orders
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="p-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {pageNumbers.map((p, i) =>
                p === "…" ? (
                  <span key={`gap-${i}`} className="px-2 text-slate-400">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    aria-current={currentPage === p ? "page" : undefined}
                    className={`min-w-[40px] px-3 py-2 rounded-lg text-sm transition-colors ${currentPage === p
                      ? "bg-[#b07d17] text-white font-semibold"
                      : "border border-slate-300 bg-white hover:bg-slate-50 text-slate-700"
                      }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="p-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </nav>
        )}
      </div>

      {/* Modals */}
      {statusModalOpen && selectedOrder && (
        <StatusChangeModal
          isOpen={statusModalOpen}
          onClose={() => setStatusModalOpen(false)}
          order={selectedOrder}
          refreshOrders={fetchOrders}
        />
      )}
      {exchangeModalOpen && selectedOrder && (
        <ExchangeStatusModal
          isOpen={exchangeModalOpen}
          onClose={() => setExchangeModalOpen(false)}
          order={selectedOrder}
          refreshOrders={fetchOrders}
        />
      )}
    </div>
  );
};

export default AdminOrderList;